import React, { useEffect, useState } from 'react';
import { TextField, TextFieldProps } from '@mui/material';

type Props = Omit<TextFieldProps, 'value' | 'onChange' | 'type' | 'onBlur' | 'onFocus'> & {
  value: number;
  min?: number;
  max?: number;
  /** Si true, muestra vacío cuando value===0 y el campo no está enfocado */
  emptyWhenZero?: boolean;
  onCommit: (value: number) => void;
};

/**
 * Campo numérico que permite borrar todos los dígitos mientras se edita.
 * El clamp min/max se aplica al salir del campo (blur) o Enter.
 */
const DeferredNumberField: React.FC<Props> = ({
  value,
  min = 0,
  max = Number.MAX_SAFE_INTEGER,
  emptyWhenZero = false,
  onCommit,
  onKeyDown,
  inputProps,
  ...rest
}) => {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState<string>(() => String(value));

  useEffect(() => {
    if (!focused) {
      setDraft(emptyWhenZero && value === 0 ? '' : String(value));
    }
  }, [value, focused, emptyWhenZero]);

  const clamp = (raw: string): number => {
    if (raw === '' || raw === '-') return min;
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return min;
    return Math.min(max, Math.max(min, n));
  };

  const commit = (raw: string) => {
    const next = clamp(raw);
    onCommit(next);
    setDraft(emptyWhenZero && next === 0 ? '' : String(next));
  };

  const display = focused
    ? draft
    : (emptyWhenZero && value === 0 ? '' : String(value));

  return (
    <TextField
      {...rest}
      type="text"
      inputMode="numeric"
      value={display}
      inputProps={{
        ...inputProps,
        inputMode: 'numeric',
        pattern: '[0-9]*',
      }}
      onFocus={(e) => {
        setFocused(true);
        setDraft(value === 0 && emptyWhenZero ? '' : String(value));
        requestAnimationFrame(() => e.target.select());
      }}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === '' || /^\d+$/.test(raw)) {
          setDraft(raw);
        }
      }}
      onBlur={() => {
        setFocused(false);
        commit(draft);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
        }
        onKeyDown?.(e);
      }}
    />
  );
};

export default DeferredNumberField;
